/**
 * Navigation Items Configuration
 * Defines the navigation structure with role-based visibility
 */

import {
  Dashboard as DashboardIcon,
  Psychology as SkillsIcon,
  AutoFixHigh as DirectivesIcon,
  People as UsersIcon,
  Settings as SettingsIcon,
  Business as WorkspaceIcon,
  Payment as BillingIcon,
  Hub as ConnectionsIcon,
} from '@mui/icons-material';
import { PROTECTED_ROUTES } from '@/routes/routes';
import { isConnectionConsoleEnabled } from '@/features/connections/connection-console.activation';
import { User } from '@/store/auth.store';
import type { WorkspaceRole } from '@/features/workspaces/types/workspace.types';

/**
 * Navigation Item Type
 */
export interface NavItemConfig {
  id: string;
  label: string;
  icon: React.ComponentType;
  path?: string;
  children?: NavItemConfig[];
  /** @deprecated Use requiredWorkspaceRoles for workspace-scoped gating */
  requiredRoles?: User['role'][];
  requiredWorkspaceRoles?: WorkspaceRole[];
  divider?: boolean;
}

/**
 * Navigation Items
 * Organized by section with role-based visibility
 */
export const navItems: NavItemConfig[] = [
  // Main Navigation
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: DashboardIcon,
    path: PROTECTED_ROUTES.DASHBOARD,
  },
  {
    id: 'skills',
    label: 'Skills',
    icon: SkillsIcon,
    path: PROTECTED_ROUTES.SKILLS,
  },
  {
    id: 'directives',
    label: 'Directives',
    icon: DirectivesIcon,
    path: PROTECTED_ROUTES.DIRECTIVES,
  },

  // Workspace Section
  {
    id: 'workspace',
    label: 'Workspace',
    icon: WorkspaceIcon,
    requiredWorkspaceRoles: ['OWNER', 'ADMIN'],
    divider: true,
    children: [
      {
        id: 'workspace-settings',
        label: 'Settings',
        icon: SettingsIcon,
        path: PROTECTED_ROUTES.WORKSPACE_SETTINGS,
      },
      ...(isConnectionConsoleEnabled()
        ? [
            {
              id: 'workspace-connections',
              label: 'Connections',
              icon: ConnectionsIcon,
              path: PROTECTED_ROUTES.WORKSPACE_CONNECTIONS,
              requiredWorkspaceRoles: ['OWNER', 'ADMIN'] as WorkspaceRole[],
            },
          ]
        : []),
      {
        id: 'workspace-members',
        label: 'Members',
        icon: UsersIcon,
        path: PROTECTED_ROUTES.WORKSPACE_MEMBERS,
      },
      {
        id: 'workspace-billing',
        label: 'Billing',
        icon: BillingIcon,
        path: PROTECTED_ROUTES.BILLING,
      },
    ],
  },
];

/**
 * Filter navigation items based on workspace role
 */
export const filterNavItemsByWorkspaceRole = (
  items: NavItemConfig[],
  workspaceRole: WorkspaceRole | null | undefined
): NavItemConfig[] => {
  if (!workspaceRole) {
    // When workspace role is unknown (loading), only show items with no role requirement
    return items
      .filter((item) => !item.requiredWorkspaceRoles || item.requiredWorkspaceRoles.length === 0)
      .map((item) => ({
        ...item,
        children: item.children
          ? filterNavItemsByWorkspaceRole(item.children, workspaceRole)
          : undefined,
      }));
  }

  return items
    .filter((item) => {
      if (!item.requiredWorkspaceRoles || item.requiredWorkspaceRoles.length === 0) {
        return true;
      }
      return item.requiredWorkspaceRoles.includes(workspaceRole);
    })
    .map((item) => ({
      ...item,
      children: item.children
        ? filterNavItemsByWorkspaceRole(item.children, workspaceRole)
        : undefined,
    }));
};

/**
 * Filter navigation items based on user role
 * @deprecated Use filterNavItemsByWorkspaceRole for workspace-scoped gating
 */
export const filterNavItemsByRole = (
  items: NavItemConfig[],
  userRole: User['role'] | null
): NavItemConfig[] => {
  if (!userRole) {
    return [];
  }

  return items
    .filter((item) => {
      // If no required roles, item is visible to everyone
      if (!item.requiredRoles || item.requiredRoles.length === 0) {
        return true;
      }
      // Check if user has required role
      return item.requiredRoles.includes(userRole);
    })
    .map((item) => {
      // Recursively filter children
      if (item.children) {
        return {
          ...item,
          children: filterNavItemsByRole(item.children, userRole),
        };
      }
      return item;
    });
};

/** Separate platform navigation leaves ordinary skills-first navigation unchanged. */
export const platformBetaNavItem: NavItemConfig = {
  id: 'platform-beta',
  label: 'Platform beta overview',
  icon: DashboardIcon,
  path: '/admin/beta',
};
